<div class="forge-app">

  <!-- Nav -->
  <nav class="forge-subnav">
    <a routerLink="/blacksmithing" class="forge-subnav__back">← Blacksmithing</a>
    <div class="forge-subnav__links">
      <a routerLink="/blacksmithing/journal" routerLinkActive="forge-subnav__link--active" class="forge-subnav__link">Journal</a>
      <a routerLink="/blacksmithing/my-gallery" routerLinkActive="forge-subnav__link--active" class="forge-subnav__link">My Gallery</a>
    </div>
  </nav>

  <!-- Header -->
  <div class="journal-hero">
    <div class="journal-hero__glow"></div>
    <div class="journal-hero__content">
      <h1>📔 Forge Journal</h1>
      <p class="journal-hero__streak">{{ streakLabel }}</p>
      <div class="journal-hero__stats" *ngIf="entries.length">
        <span>{{ entries.length }} session{{ entries.length !== 1 ? 's' : '' }} logged</span>
        <span class="divider">·</span>
        <a routerLink="/blacksmithing/my-gallery" class="gallery-link">
          {{ (photosByEntry | keyvalue)?.length ? 'View all photos →' : 'No photos yet' }}
        </a>
      </div>
    </div>
    <button class="btn-forge journal-hero__cta" (click)="openNewForm()">+ Log a Session</button>
  </div>

  <!-- Loading -->
  <div class="forge-loading" *ngIf="loading">Loading your journal...</div>

  <!-- Empty State -->
  <div class="journal-empty" *ngIf="!loading && !entries.length">
    <div class="journal-empty__icon">⚒️</div>
    <h3>Your forge journal is empty</h3>
    <p>Log your first session to start tracking your progress.</p>
    <button class="btn-forge" (click)="openNewForm()">Log Your First Session</button>
  </div>

  <!-- Entry List -->
  <div class="journal-list" *ngIf="!loading && entries.length">
    <div class="journal-entry" *ngFor="let entry of entries">
      <div class="journal-entry__header">
        <div class="journal-entry__date-block">
          <span class="journal-entry__date">{{ formatDate(entry.date) }}</span>
          <span class="journal-entry__module" *ngIf="entry.moduleId">
            {{ getModuleTitle(entry.moduleId) }}
          </span>
        </div>
        <div class="journal-entry__actions">
          <button class="btn-icon" (click)="openEditForm(entry)" title="Edit">✏️</button>
          <button class="btn-icon btn-icon--danger" (click)="deleteEntry(entry)" title="Delete">🗑️</button>
        </div>
      </div>

      <div class="journal-entry__meta" *ngIf="entry.project || entry.heats || entry.sessionLength">
        <span class="meta-chip" *ngIf="entry.project">🔨 {{ entry.project }}</span>
        <span class="meta-chip" *ngIf="entry.heats">🔥 {{ entry.heats }} heats</span>
        <span class="meta-chip" *ngIf="entry.sessionLength">⏱ {{ entry.sessionLength }}</span>
      </div>

      <div class="journal-entry__body">
        <div class="journal-field" *ngIf="entry.whatWentWell">
          <span class="journal-field__label">✓ What went well</span>
          <p class="journal-field__text">{{ entry.whatWentWell }}</p>
        </div>
        <div class="journal-field" *ngIf="entry.challenges">
          <span class="journal-field__label">⚡ Challenges</span>
          <p class="journal-field__text">{{ entry.challenges }}</p>
        </div>
        <div class="journal-field" *ngIf="entry.notes">
          <span class="journal-field__label">📝 Notes</span>
          <p class="journal-field__text">{{ entry.notes }}</p>
        </div>
      </div>

      <!-- Photos for this entry -->
      <div class="entry-photos" *ngIf="getPhotos(entry.id).length">
        <div class="entry-photos__grid">
          <div
            class="entry-photo"
            *ngFor="let photo of getPhotos(entry.id)"
            (click)="openLightbox(photo)"
          >
            <img [src]="photo.downloadUrl" [alt]="photo.caption || 'Forge photo'" />
            <div class="entry-photo__overlay">
              <span *ngIf="photo.caption" class="entry-photo__caption">{{ photo.caption }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Add more photos to existing entry -->
      <div class="entry-add-photos">
        <label class="btn-add-photo">
          + Add Photos
          <input
            type="file"
            accept="image/*"
            multiple
            hidden
            (change)="addPhotosToEntry($event, entry)"
          />
        </label>
      </div>
    </div>
  </div>

  <!-- ── Form Modal ──────────────────────────────────────────────────────── -->
  <div class="forge-modal-backdrop" *ngIf="showForm" (click)="closeForm()">
    <div class="forge-modal" (click)="$event.stopPropagation()">
      <div class="forge-modal__header">
        <h3>{{ editingEntry ? 'Edit Session' : 'Log a Forge Session' }}</h3>
        <button class="forge-modal__close" (click)="closeForm()">✕</button>
      </div>

      <div class="forge-modal__body">
        <!-- Date (required) -->
        <div class="form-field">
          <label class="form-label">Date <span class="required">*</span></label>
          <input type="date" class="form-input" [(ngModel)]="formData.date" />
        </div>

        <!-- Module -->
        <div class="form-field">
          <label class="form-label">Module <span class="optional">(optional)</span></label>
          <select class="form-input" [(ngModel)]="formData.moduleId">
            <option value="">— Select a module —</option>
            <option *ngFor="let mod of modules" [value]="mod.id">
              {{ mod.number ? 'Module ' + mod.number + ': ' : '' }}{{ mod.title }}
            </option>
          </select>
        </div>

        <!-- Project -->
        <div class="form-field">
          <label class="form-label">What did you make? <span class="optional">(optional)</span></label>
          <input type="text" class="form-input" [(ngModel)]="formData.project" placeholder="e.g. S-hook, forged leaf, nail..." />
        </div>

        <div class="form-row">
          <!-- Heats -->
          <div class="form-field">
            <label class="form-label">Heats <span class="optional">(optional)</span></label>
            <input type="number" class="form-input" [(ngModel)]="formData.heats" placeholder="e.g. 12" min="1" />
          </div>
          <!-- Session length -->
          <div class="form-field">
            <label class="form-label">Session length <span class="optional">(optional)</span></label>
            <input type="text" class="form-input" [(ngModel)]="formData.sessionLength" placeholder="e.g. 2 hours" />
          </div>
        </div>

        <!-- What went well -->
        <div class="form-field">
          <label class="form-label">What went well <span class="optional">(optional)</span></label>
          <textarea class="form-input form-textarea" [(ngModel)]="formData.whatWentWell" placeholder="Tapers were consistent, heat colors are clicking..."></textarea>
        </div>

        <!-- Challenges -->
        <div class="form-field">
          <label class="form-label">Challenges <span class="optional">(optional)</span></label>
          <textarea class="form-input form-textarea" [(ngModel)]="formData.challenges" placeholder="Upsetting kept bending instead of thickening..."></textarea>
        </div>

        <!-- Notes -->
        <div class="form-field">
          <label class="form-label">Notes <span class="optional">(optional)</span></label>
          <textarea class="form-input form-textarea form-textarea--tall" [(ngModel)]="formData.notes" placeholder="Anything else worth remembering..."></textarea>
        </div>

        <!-- Photo Upload -->
        <div class="form-field">
          <label class="form-label">Photos <span class="optional">(optional, up to 10)</span></label>
          <label class="photo-upload-btn">
            📷 Choose Photos
            <input type="file" accept="image/*" multiple hidden (change)="onFilesSelected($event)" />
          </label>

          <div class="photo-previews" *ngIf="filePreviews.length">
            <div class="photo-preview" *ngFor="let preview of filePreviews; let i = index">
              <img [src]="preview" alt="Preview" />
              <button class="photo-preview__remove" (click)="removeSelectedFile(i)">✕</button>
            </div>
          </div>

          <div class="upload-progress" *ngIf="saving && uploadProgress > 0 && uploadProgress < 100">
            <div class="upload-progress__bar" [style.width.%]="uploadProgress"></div>
            <span>Uploading photos... {{ uploadProgress }}%</span>
          </div>
        </div>

        <p class="form-error" *ngIf="formError">{{ formError }}</p>
      </div>

      <div class="forge-modal__footer">
        <button class="btn-secondary" (click)="closeForm()">Cancel</button>
        <button class="btn-forge" (click)="saveEntry()" [disabled]="saving || !formData.date">
          {{ saving ? 'Saving...' : (editingEntry ? 'Save Changes' : 'Log Session') }}
        </button>
      </div>
    </div>
  </div>

  <!-- ── Lightbox ────────────────────────────────────────────────────────── -->
  <div class="lightbox" *ngIf="lightboxPhoto" (click)="closeLightbox()">
    <div class="lightbox__inner" (click)="$event.stopPropagation()">
      <button class="lightbox__close" (click)="closeLightbox()">✕</button>
      <img class="lightbox__img" [src]="lightboxPhoto.downloadUrl" [alt]="lightboxPhoto.caption || ''" />
      <div class="lightbox__footer">
        <ng-container *ngIf="editingCaptionId !== lightboxPhoto.id">
          <span class="lightbox__caption">{{ lightboxPhoto.caption || 'No caption' }}</span>
          <div class="lightbox__actions">
            <button class="btn-sm" (click)="startEditCaption(lightboxPhoto)">Edit caption</button>
            <button class="btn-sm btn-sm--danger" (click)="deletePhotoFromLightbox(lightboxPhoto)">Delete photo</button>
          </div>
        </ng-container>
        <ng-container *ngIf="editingCaptionId === lightboxPhoto.id">
          <input class="form-input caption-input" [(ngModel)]="captionDraft" placeholder="Add a caption..." />
          <div class="lightbox__actions">
            <button class="btn-forge btn-sm" (click)="saveCaption(lightboxPhoto)">Save</button>
            <button class="btn-sm" (click)="editingCaptionId = null">Cancel</button>
          </div>
        </ng-container>
      </div>
    </div>
  </div>

</div>
